<template>
  <AppView :show-bottom-bar="!error && !loading" padd-bottom>
    <TopNav
      type="back"
      :show-notification-icon="false"
      :show-back-button="showBackButton"
      slot="top-app-view"
    />
    <div class="px-body">
      <div v-if="error" class="tw-text-center">
        <p class="tw-text-xs opacity-54 tw-py-18 tw-text-center" v-html="error"></p>
      </div>
      <div v-else-if="loading">
        <p class="tw-text-xs opacity-54 tw-py-18 tw-text-center">Loading...</p>
      </div>
      <div v-else>
        <transition name="new" mode="out-in" appear>
          <div class="tw-text-center tw-max-w-lg tw-mx-auto">
            <UserPhoto
              :photo="user.photo"
              :user-name="user"
              photo-size="tw-w-20 tw-h-20 tw-inline-block"
            />
            <h4 class="font-title tw-text-xl leading-24 tw-mt-3">{{ userFullName }}</h4>
            <p v-if="user.profession?.profession && !isKengen" class="tw-text-sm tw-text-center opacity-54 tw-text-black tw-mt-2 tw-mr-2 leading-19 tw-px-2 tw-inline-block">
              Profession: <b>{{ user.profession.profession }}</b>
            </p>
            <template v-if="user.department">
              <p class="tw-text-sm tw-text-center opacity-54 tw-text-black tw-mt-2 leading-19 tw-px-2 tw-inline-block">
                Department: <b>{{ user.department?.departments }}</b>
              </p>
            </template>

            <template v-if="user.regional_area">
              <p class="tw-text-sm tw-text-center opacity-54 tw-text-black tw-mt-2 leading-19 tw-px-2 tw-inline-block">
                Region: <b>{{ user.regional_area?.region }}</b>
              </p>
            </template>

            <template v-if="user.sub_area">
              <p class="tw-text-sm tw-text-center opacity-54 tw-text-black tw-mt-2 leading-19 tw-px-2 tw-inline-block">
                Sub-area: <b>{{ user.sub_area?.sub_area }}</b>
              </p>
            </template>

            <template v-if="user.section">
              <p class="tw-text-sm tw-text-center opacity-54 tw-text-black tw-mt-2 leading-19 tw-px-2 tw-inline-block">
                Section: <b>{{ user.section?.sections }} - {{ user.division?.divisions }} division</b>
              </p>
            </template>

            <p class="tw-text-sm tw-text-center opacity-54 tw-text-black leading-19 tw-pt-2 tw-mb-1">
              availability
            </p>
            <p
              class="tw-text-xs tw-mr-2 tw-text-center opacity-54 tw-text-white leading-19 tw-bg-gray-700 tw-rounded-full tw-px-2 tw-inline-block tw-mb-1"
              v-for="(availability, index) in user.availabilities"
              :key="index"
            >
              {{ availability.availability }}
            </p>
            <p class="tw-text-base leading-19 opacity-54 tw-mt-6">{{ user.bio }}</p>
          </div>
        </transition>
      </div>
    </div>
    <div
      slot="bottom-app-view"
      class="px-body tw-py-4"
      v-if="isMentee && canRequest && !isArchivedProgram"
    >
      <BaseButton
        :class="[
          'tw-py-3 tw-bg-app-primary-orange tw-text-app-primary-orange-contrast tw-block lg:tw-mx-auto lg:tw-max-w-lg tw-border-0 tw-w-full tw-shadow-xl',
          {'tw-bg-green-400': requestSuccess}
        ]"
        :text="btnText"
        :disabled="loading || isSendingRequest || isMyMentor || !canRequestMentor"
        @click="sendMentorRequest"
      />
    </div>
    <!-- [TODO] ACCEPT/REJECT MENTEE REQUEST FROM HERE -->
    <div
      slot="bottom-app-view"
      class="px-body tw-py-4 tw-flex"
      v-if="isMentor && isMenteeRequest && !isArchivedProgram"
    >
      <BaseButton
        :class="[
          'tw-py-3 tw-bg-app-deep-blue tw-text-white tw-block lg:tw-max-w-lg tw-border-0 tw-flex-grow tw-mr-4 tw-shadow-xl',
          {'tw-bg-green-400': requestSuccess}
        ]"
        :text="acceptBtnText"
        :disabled="loading || isSendingRequest || isMyMentor"
        @click="acceptMenteeRequest"
      />
      <BaseButton
        :class="[
          'tw-py-3 tw-bg-red-200 tw-text-red-600 tw-block lg:tw-max-w-lg tw-border-0 tw-flex-grow tw-shadow-xl',
          {'tw-bg-green-400': requestSuccess}
        ]"
        :text="declineBtnText"
        :disabled="loading || isSendingRequest || isMyMentor"
        @click="declineMenteeRequest"
      />
    </div>
  </AppView>
</template>

<script>
export default {
  name: 'UserBio',
  props: ['id', 'slug', 'showBackButton'],
  data() {
    return {
      user: undefined,
      loading: true,
      error: undefined,
      isSendingRequest: false,
      requestSuccess: false,
      canRequest: true,
      goalid: undefined,
      buttonText: 'Send Request',
      acceptBtnText: 'Accept Request',
      declineBtnText: 'I am unavailable',
    };
  },
  computed: {
    isMenteeRequest() {
      if (this.isMentee) return false;
      return this.$store.getters.newMentees.find((menteeRequest) => menteeRequest.menteeid === this.id);
    },
    userFullName() {
      return `${this.user.name} ${this.user.last_name}`;
    },
    isMyMentor() {
      if (this.isMentee) {
        return !!this.$store.getters.getMentor(this.id);
      }
      return false;
    },
    btnText() {
      if (this.isMyMentor) {
        return `${this.userFullName} is your mentor`;
      }

      if (this.isSequentialProgram && this.$store.getters.myMentors?.length > 0) {
        return 'You can have only one mentor in this program';
      }

      return this.buttonText;
    },
    canRequestMentor() {
      if (this.isSequentialProgram) {
        return !(this.$store.getters.myMentors?.length > 0) && this.isMentee && this.id;
      }

      return true;
    },
  },
  methods: {
    sendMentorRequest() {
      if (!this.canRequestMentor) return false;
      this.isSendingRequest = true;
      this.buttonText = 'Sending...';

      const data = {
        mentorid: this.id,
        goalid: this.goalid,
      };

      const dispatchEndpoint = 'sendProgramMentorRequest';

      this.$store.dispatch(dispatchEndpoint, data)
        .catch((err) => {
          const errorMessage = 'Error in sending your request. Try again later';
          console.warning(errorMessage, err);
          this.$toasted.global.appError({ errorMessage });
          this.error = errorMessage;
        })
        .then(([res, errorMessage]) => {
          if (!res) {
            this.$toasted.global.appError({ errorMessage });
            this.buttonText = 'Send Request';
            this.isSendingRequest = false;
          } else {
            // Tell the mentee that he waits?
            this.$toasted.success(
              `Request sent to ${this.userFullName}!`,
              {
                duration: 5000,
                position: 'top-right',
              },
            );
            this.requestSuccess = true;
            this.buttonText = 'Request sent!';
            this.isSendingRequest = false;

            // ? redirect back?
            this.$router.push({
              name: 'menteeMentorsView',
            });

            // get prompt for user
            this.$store.dispatch('getCurrentUserJourneyAction');
          }
        });

      return true;
    },
    async acceptMenteeRequest() {
      this.acceptBtnText = 'Working...';
      this.isSendingRequest = true;

      const dispatchEndpoint = 'acceptProgramMenteeRequest';
      const [result, message] = await this.$store.dispatch(dispatchEndpoint, this.id);
      if (!result) {
        this.$toasted.global.appError({ errorMessage: message });
        this.acceptBtnText = 'Accept Request';
        this.isSendingRequest = false;
        return false;
      }

      this.$toasted.success(message, {
        duration: 3000,
        position: 'top-right',
      });
      this.acceptBtnText = 'Accepted!';
      this.requestSuccess = true;

      // get prompt for user
      this.$store.dispatch('getCurrentUserJourneyAction');

      return true;
    },
    async declineMenteeRequest() {
      this.declineBtnText = 'Working...';
      this.isSendingRequest = true;

      const dispatchEndpoint = 'declineProgramMenteeRequest';
      const [result, message] = await this.$store.dispatch(dispatchEndpoint, this.id);

      if (!result) {
        this.$toasted.global.appError({ errorMessage: message });
        this.isSendingRequest = false;
        this.declineBtnText = 'Decline Request';
        return false;
      }

      this.$toasted.success(message, {
        duration: 3000,
        position: 'top-right',
      });
      this.declineBtnText = 'Declined!';
      this.requestSuccess = true;

      // get prompt for user
      this.$store.dispatch('getCurrentUserJourneyAction');

      return true;
    },
  },
  async mounted() {
    this.$store.commit('isFlatView', false);

    // (mentor) redirect to app if mentee request not found
    if (this.$route.name === 'menteeRequest' && this.isMentor) {
      if (!this.isMenteeRequest) {
        // confirm with server
        await this.$store.dispatch('getMyPendingProgramMenteesRequests');
        await this.$nextTick();
        await this.$nextTick();

        if (!this.isMenteeRequest) {
          this.$toasted.global.appError({ errorMessage: 'You have no such request. Redirecting you back...' });
          await this.$nextTick();
          this.$router.push({ name: 'app' });
          return false;
        }
      }
    }

    if (this.isMentee) {
      if (this.isSequentialProgram) {
        const [currentProgramModules, upcomingProgramModules, requestedMentors] = await Promise.all([
          this.$store.dispatch('getActiveProgramModules', []),
          this.$store.dispatch('getUpcomingProgramModules', []),
          this.$store.dispatch('getMyProgramPendingMentorsRequests'),
        ]);

        this.goalid = currentProgramModules[0]?.id || upcomingProgramModules[0]?.id;
        this.canRequest = requestedMentors?.length === 0 && this.$store.getters.myMentors?.length === 0;
      } else {
        this.goalid = this.$route.query.goalid;
      }

      await this.$nextTick();

      if (!this.goalid) {
        const eMessage = 'Error loading page details';
        console.error('Goal Id not found', eMessage);
        this.$toasted.global.appError({ errorMessage: eMessage });
        this.error = eMessage;

        return false;
      }
    }

    const errorMessage = `Error in getting ${this.isMentor ? 'mentee\'s' : 'mentor\'s'} information`;
    return this.$store.dispatch('getUserData', this.id)
      .catch((err) => {
        console.error(errorMessage, err);
        this.$toasted.global.appError({ errorMessage });
        this.error = errorMessage;
      })
      .then(async (user) => {
        if (!user) return false;
        this.user = user;
        this.loading = false;
        this.error = undefined;

        if (this.slug === 'accept' && this.isMentor) {
          // accept then go to my-mentees(app)
          const result = await this.acceptMenteeRequest();

          if (result) {
            this.$router.push({ name: 'app' });
          }
        }
        if ((this.slug === 'reject' || this.slug === 'decline') && this.isMentor) {
          // decline then go to app
          const result = await this.declineMenteeRequest();

          if (result) {
            this.$router.push({ name: 'app' });
          }
        }
        return user;
      });
  },
};
</script>

<style>

</style>
